import { useMemo } from "react";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";

import { CardContent } from "@/components/ui/card";

import type { ReactNode } from "react";
import type { ProcessedAssignment, MonthlyCalendarRow } from "../type";
import { getMonthlyBarH, getMonthlyCellH, ZOOM_MIN } from "../utils";
import { useHolidays } from "@/hooks/use-holidays";
import type { AvailabilityMap } from "@/hooks/use-resource-availability";

import { BAR_AREA_TOP } from "./constant";
import { splitIntoWeeks } from "./month/helper";
import RightColumn from "./month/right-column";
import { resolveMonthAvailability } from "./availability-bands";
import type { BarRenderContext } from "./month/right-column";

dayjs.extend(isToday);

const BAR_GAP = 3; // px — gap between bars

// ── Types ─────────────────────────────────────────────────────────────────────
interface MonthlyCalendarGridProps {
  days: Date[];
  /**
   * Rows whose assignments are merged into the calendar. The month view has no per-row
   * lanes, so which rows appear is chosen with the toolbar Show picker, not a left column.
   */
  rows: MonthlyCalendarRow[];
  zoomLevel?: number;
  /**
   * Optional render prop passed through to RightColumn.
   * Use this to provide custom bar inner content (e.g. TechnicianBarContent).
   */
  renderBarContent?: (ctx: BarRenderContext) => ReactNode;
  /**
   * Optional per-resource AM/PM availability (tech/machine tabs). When provided, the
   * merged calendar washes a day's AM/PM red where every displayed resource is
   * unavailable. Omitted by the job tab. (WOR-2187 R3.)
   */
  availabilityMap?: AvailabilityMap;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function MonthlyCalendarGrid({
  days,
  rows,
  zoomLevel = ZOOM_MIN,
  renderBarContent,
  availabilityMap
}: MonthlyCalendarGridProps) {
  // Zoom-scaled dimensions
  const BAR_H = getMonthlyBarH(zoomLevel);
  const CELL_H = getMonthlyCellH(zoomLevel);
  const MAX_BARS = Math.max(
    1,
    Math.floor((CELL_H - BAR_AREA_TOP - 8) / (BAR_H + BAR_GAP))
  );
  // At low zoom (≤ 30%), render compact single-line bars; above that, render full card
  const isCompact = BAR_H < 50;

  // Flatten all assignments from all rows into one pool for the calendar
  const allAssignments: ProcessedAssignment[] = rows.flatMap(
    (row) => row.assignments
  );

  const weeks = splitIntoWeeks(days);

  // Aggregate per-day AM/PM unavailability across the displayed resources for the
  // merged calendar; undefined on the job tab (no availabilityMap → no red). (WOR-2187 R3.)
  const holidays = useHolidays();
  const availabilityByDay = useMemo(
    () =>
      availabilityMap
        ? resolveMonthAvailability(
            days,
            rows.map((r) => Number(r.id)),
            availabilityMap,
            holidays
          )
        : undefined,
    [availabilityMap, days, rows, holidays]
  );

  return (
    <CardContent className="aero:p-0">
      {/* ── Full-width calendar: day-name header + scrolling weeks ───── */}
      <div
        className="aero:flex aero:overflow-hidden"
        style={{ height: "calc(100vh - 320px)", minHeight: 300 }}
      >
        <RightColumn
          days={days}
          weeks={weeks}
          allAssignments={allAssignments}
          size={{
            barHeight: BAR_H,
            cellHeight: CELL_H,
            maxBars: MAX_BARS,
            barGap: BAR_GAP
          }}
          isCompact={isCompact}
          zoomLevel={zoomLevel}
          renderBarContent={renderBarContent}
          availabilityByDay={availabilityByDay}
        />
      </div>
    </CardContent>
  );
}
